import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ApiClient } from '@center-technologies/shared-api-clients';
import { fetchVirtualPeriods, VirtualPeriod } from '@center-platform/spheres/identity';
import {
  getLevels,
  getDefaultLevel,
  getDateWindow,
  TimeframeLevelInfo,
  TimeframeNestingLevel,
  DateWindow,
} from '../PeriodSelector/periodSelectorUtils';

export interface UsePeriodGridParams {
  graphClient: ApiClient | undefined;
  timeframeId: string | undefined;
  nestedTimeframes?: TimeframeNestingLevel[];
  scheduleDefinition?: { rrule?: string; type?: string } | null;
  searchTerm: string;
}

export interface PeriodGridState {
  levels: string[];
  activeLevel: string;
  windowLabel: string;
  filtered: VirtualPeriod[];
  loading: boolean;
  error: string | null;
  handleLevelChange: (level: string) => void;
  handlePrev: () => void;
  handleNext: () => void;
  handleNow: () => void;
}

function formatWindowLabel(from: Date, to: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
  return `${from.toLocaleDateString(undefined, opts)} \u2013 ${to.toLocaleDateString(undefined, opts)}`;
}

function shiftWindow(win: DateWindow, direction: -1 | 1): DateWindow {
  const from = new Date(win.from);
  const to = new Date(win.to);
  from.setMonth(from.getMonth() + direction * win.stepMonths);
  to.setMonth(to.getMonth() + direction * win.stepMonths);
  return { from, to, stepMonths: win.stepMonths };
}

export function usePeriodGrid({
  graphClient,
  timeframeId,
  nestedTimeframes,
  scheduleDefinition,
  searchTerm,
}: UsePeriodGridParams): PeriodGridState {
  const [activeLevel, setActiveLevel] = useState('');
  const [dateWindow, setDateWindow] = useState<DateWindow>(() => getDateWindow('month'));
  const [periods, setPeriods] = useState<VirtualPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevTimeframeIdRef = useRef(timeframeId);

  // Derive levels from timeframe structure — stable for the same timeframeId
  const levels = useMemo(() => {
    if (!timeframeId) return [];
    const info: TimeframeLevelInfo = { nestedTimeframes, scheduleDefinition };
    return getLevels(info);
  }, [timeframeId]);

  // Reset level and date window when timeframe changes
  useEffect(() => {
    if (levels.length === 0) {
      setActiveLevel('');
      return;
    }
    const changed = prevTimeframeIdRef.current !== timeframeId;
    prevTimeframeIdRef.current = timeframeId;

    if (changed || !activeLevel || !levels.includes(activeLevel)) {
      const def = getDefaultLevel(levels);
      setActiveLevel(def);
      setDateWindow(getDateWindow(def));
    }
  }, [levels, timeframeId, activeLevel]);

  // Fetch virtual periods when key parameters change
  useEffect(() => {
    if (!graphClient || !timeframeId || !activeLevel) {
      setPeriods([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchVirtualPeriods(graphClient, {
      rootTimeframeId: timeframeId,
      level: activeLevel as 'year' | 'quarter' | 'month' | 'week',
      from: dateWindow.from,
      to: dateWindow.to,
    })
      .then((result) => {
        if (!cancelled) {
          setPeriods(result.periods);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('fetchPeriods');
          setPeriods([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [graphClient, timeframeId, activeLevel, dateWindow]);

  // Client-side search filtering on period names
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return periods;
    const lower = searchTerm.toLowerCase();
    return periods.filter((p) => p.name.toLowerCase().includes(lower));
  }, [periods, searchTerm]);

  const handleLevelChange = useCallback((level: string) => {
    setActiveLevel(level);
    setDateWindow(getDateWindow(level));
  }, []);

  const handlePrev = useCallback(() => {
    setDateWindow((prev) => shiftWindow(prev, -1));
  }, []);

  const handleNext = useCallback(() => {
    setDateWindow((prev) => shiftWindow(prev, 1));
  }, []);

  const handleNow = useCallback(() => {
    setDateWindow(getDateWindow(activeLevel));
  }, [activeLevel]);

  const windowLabel = useMemo(
    () => formatWindowLabel(dateWindow.from, dateWindow.to),
    [dateWindow],
  );

  return {
    levels,
    activeLevel,
    windowLabel,
    filtered,
    loading,
    error,
    handleLevelChange,
    handlePrev,
    handleNext,
    handleNow,
  };
}
